// "Can't find it? Enter the address manually" under an address field: boxes
// for the first line, second line, town and postcode, joined into the field's
// one-line address, for an address Google doesn't suggest (a new build, say)
// or when Google can't be reached. Replaces the Places widget with the plain
// input holding the joined address, which stays editable.

import { escapeHtml } from '../format';

export interface ManualAddress { line1: string; line2: string; town: string; postcode: string }

const UK_POSTCODE = /^([A-Z]{1,2}\d[A-Z\d]?)\s*(\d[A-Z]{2})$/i;
const ANY_POSTCODE = /\b([A-Z]{1,2}\d[A-Z\d]?)\s*(\d[A-Z]{2})\b/i;

export function ukPostcode(text: string): string | null {
  const m = UK_POSTCODE.exec(text.trim());
  return m ? `${m[1]} ${m[2]}`.toUpperCase() : null;
}

export function splitAddress(value: string): ManualAddress {
  const pc = ANY_POSTCODE.exec(value);
  const postcode = pc ? `${pc[1]} ${pc[2]}`.toUpperCase() : '';
  const parts = value.replace(ANY_POSTCODE, '').split(',').map((x) => x.trim())
    .filter((x) => x && !/^(uk|united kingdom|england|wales|scotland|northern ireland)$/i.test(x));
  return { line1: parts[0] ?? '', line2: parts.length > 2 ? parts.slice(1, -1).join(', ') : '', town: parts.length > 1 ? parts[parts.length - 1] : '', postcode };
}

export function joinAddress(a: ManualAddress): string {
  return [a.line1, a.line2, a.town, a.postcode].map((x) => x.trim()).filter(Boolean).join(', ');
}

export function manualProblem(a: ManualAddress, ukOnly: boolean): string {
  if (!a.line1.trim()) return 'Enter the first line of the address.';
  if (!a.town.trim()) return 'Enter the town or city.';
  if (ukOnly && !ukPostcode(a.postcode)) return a.postcode.trim() ? 'That doesn’t look like a UK postcode.' : 'Enter the postcode.';
  return '';
}

export function attachManualAddress(input: HTMLInputElement, onChange: (value: string) => void, ukOnly: boolean): void {
  if (input.dataset.manualAttached) return;
  input.dataset.manualAttached = '1';
  const holder = document.createElement('div');
  holder.className = 'manual-address';
  (input.closest('.address-row') ?? input).insertAdjacentElement('afterend', holder);
  const id = `${input.id || 'address'}-manual`;
  const closed = () => {
    holder.innerHTML = `<button type="button" class="link-btn manual-address-open" aria-expanded="false" aria-controls="${escapeHtml(id)}">Can’t find it? Enter the address manually</button>`;
  };
  const open = () => {
    const a = splitAddress(input.value);
    const box = (name: keyof ManualAddress, label: string, auto: string) =>
      `<label class="manual-address-field"><span>${escapeHtml(label)}</span><input class="control" type="text" data-manual="${name}" autocomplete="${auto}" value="${escapeHtml(a[name])}" /></label>`;
    holder.innerHTML = `<div class="manual-address-box" id="${escapeHtml(id)}" role="group" aria-label="Address, entered manually">
      ${box('line1', 'Building and street', 'address-line1')}${box('line2', 'Second line (optional)', 'address-line2')}
      ${box('town', 'Town or city', 'address-level2')}${box('postcode', ukOnly ? 'Postcode' : 'Postcode (if there is one)', 'postal-code')}
      <p class="manual-address-error" role="alert" hidden></p>
      <div class="manual-address-actions"><button type="button" class="btn btn-soft btn-sm manual-address-use">Use this address</button><button type="button" class="link-btn manual-address-cancel">Cancel</button></div>
    </div>`;
    holder.querySelector<HTMLInputElement>('[data-manual="line1"]')?.focus();
  };
  const read = (): ManualAddress => {
    const v = (name: string) => holder.querySelector<HTMLInputElement>(`[data-manual="${name}"]`)?.value ?? '';
    const postcode = v('postcode');
    return { line1: v('line1'), line2: v('line2'), town: v('town'), postcode: ukPostcode(postcode) ?? postcode };
  };
  const use = () => {
    const a = read();
    const problem = manualProblem(a, ukOnly);
    const err = holder.querySelector<HTMLElement>('.manual-address-error');
    if (problem) { if (err) { err.textContent = problem; err.hidden = false; } return; }
    // The plain input, holding the address, in place of Google's widget.
    const widget = input.nextElementSibling;
    if (widget && widget.tagName === 'GMP-PLACE-AUTOCOMPLETE') widget.remove();
    input.style.display = '';
    input.value = joinAddress(a);
    closed();
    onChange(input.value);
  };
  holder.addEventListener('click', (e) => {
    const t = (e.target as HTMLElement).closest('button');
    if (!t) return;
    e.preventDefault();
    if (t.classList.contains('manual-address-open')) open();
    else if (t.classList.contains('manual-address-use')) use();
    else if (t.classList.contains('manual-address-cancel')) closed();
  });
  holder.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && (e.target as HTMLElement).matches('[data-manual]')) { e.preventDefault(); use(); }
  });
  closed();
}
